import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Archive, BookOpen, Pencil, Plus, RotateCcw, Wand2 } from 'lucide-react';
import { agentApi } from '../agentApi';
import { AGENT_ADMIN_ROLES } from '../agentContextMap';
import { timeAgo } from '../format';
import MarkdownText from '../MarkdownText';
import { usePmsDataRefresh } from '../dataSync';

const SOURCE_LABEL = { BUILTIN: 'Built-in', USER: 'Team', AGENT: 'Agent-authored' };
const EMPTY_FORM = { slug: '', name: '', description: '', whenToUse: '', instructions: '', tools: '', manualMinutes: '' };

function SkillForm({ initial, creating, toolNames, onCancel, onSaved }) {
  const [form, setForm] = useState(initial);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const save = async () => {
    setSaving(true);
    const body = {
      slug: form.slug.trim(),
      name: form.name.trim(),
      description: form.description.trim(),
      whenToUse: form.whenToUse.trim() || null,
      instructions: form.instructions,
      tools: form.tools
        .split(',')
        .map((tool) => tool.trim())
        .filter(Boolean),
      ...(form.manualMinutes !== '' ? { manualMinutes: Number(form.manualMinutes) } : {}),
    };
    try {
      const { skill } = creating ? await agentApi.createSkill(body) : await agentApi.updateSkill(initial.slug, body);
      toast.success(creating ? 'Skill created' : `Saved as version ${skill.version}`);
      onSaved(skill);
    } catch (error) {
      toast.error(error.issues?.length ? error.issues.map((issue) => `${issue.path}: ${issue.message}`).join('; ') : error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dagent-form">
      <div className="dagent-form__grid">
        <label>
          Name
          <input className="dagent-input" value={form.name} onChange={set('name')} placeholder="Late check-out policy" />
        </label>
        <label>
          Slug
          <input className="dagent-input dagent-mono" value={form.slug} onChange={set('slug')} disabled={!creating} placeholder="late-checkout-policy" />
        </label>
      </div>
      <label>
        Description
        <input className="dagent-input" value={form.description} onChange={set('description')} placeholder="What the skill does, in one sentence" />
      </label>
      <label>
        When to use
        <input className="dagent-input" value={form.whenToUse} onChange={set('whenToUse')} placeholder="The user asks about…" />
      </label>
      <div className="dagent-form__row">
        <span className="dagent-label">Instructions</span>
        <button type="button" className="dagent-link-btn" onClick={() => setPreview((value) => !value)}>
          {preview ? 'Edit' : 'Preview'}
        </button>
      </div>
      {preview ? (
        <div className="dagent-preview-box">
          <MarkdownText text={form.instructions} />
        </div>
      ) : (
        <textarea
          className="dagent-input dagent-mono"
          rows={14}
          value={form.instructions}
          onChange={set('instructions')}
          placeholder={'# Goal\n\n## Steps\n1. Call list_arrivals …\n2. …\n\n## Output\nTable: …'}
        />
      )}
      <div className="dagent-form__grid">
        <label>
          Tools (comma separated)
          <input className="dagent-input dagent-mono" list="dagent-tool-names" value={form.tools} onChange={set('tools')} />
          <datalist id="dagent-tool-names">
            {toolNames.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
        </label>
        <label>
          Minutes by hand
          <input className="dagent-input" type="number" min="0" value={form.manualMinutes} onChange={set('manualMinutes')} />
        </label>
      </div>
      <div className="dagent-form__actions">
        <button type="button" className="dagent-btn dagent-btn--ghost" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="dagent-btn dagent-btn--primary" onClick={save} disabled={saving}>
          {creating ? 'Create skill' : 'Save new version'}
        </button>
      </div>
    </div>
  );
}

function SkillsManager({ user, toolNames = [], onUseSkill }) {
  const isAdmin = AGENT_ADMIN_ROLES.includes(user.role);
  const [skills, setSkills] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState('view');

  const load = useCallback(async () => {
    try {
      setSkills((await agentApi.listSkills(showArchived)).skills);
    } catch (error) {
      toast.error(error.message);
    }
  }, [showArchived]);

  useEffect(() => {
    load();
  }, [load]);

  // A skill saved (or reverted) by the agent shows up without reopening the library.
  usePmsDataRefresh(['skills'], load);

  const open = async (slug) => {
    try {
      setSelected((await agentApi.getSkill(slug)).skill);
      setMode('view');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const canEdit = (skill) => isAdmin || (skill.source !== 'BUILTIN' && skill.createdById === user.id);

  const archive = async () => {
    try {
      await agentApi.archiveSkill(selected.slug);
      toast.success('Skill archived');
      setSelected(null);
      load();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const restore = async (version) => {
    try {
      const { skill } = await agentApi.restoreSkill(selected.slug, version);
      toast.success(`Version ${version} restored as version ${skill.version}`);
      setSelected(skill);
      load();
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="dagent-skills">
      <div className="dagent-skills__list">
        <div className="dagent-panel__bar">
          <h3>Skills</h3>
          <span className="dagent-spacer" />
          <label className="dagent-check-all">
            <input type="checkbox" checked={showArchived} onChange={(event) => setShowArchived(event.target.checked)} /> Archived
          </label>
          <button
            type="button"
            className="dagent-btn dagent-btn--primary dagent-btn--sm"
            onClick={() => {
              setSelected(null);
              setMode('create');
            }}
          >
            <Plus size={14} aria-hidden="true" /> New skill
          </button>
        </div>
        <p className="dagent-muted dagent-small">Reusable procedures the agent loads when a request matches. Every edit is kept as a version.</p>
        <div className="dagent-skill-cards">
          {skills.map((skill) => (
            <button
              key={skill.slug}
              type="button"
              className={`dagent-skill-card ${selected?.slug === skill.slug ? 'is-active' : ''} ${skill.status === 'ARCHIVED' ? 'is-archived' : ''}`}
              onClick={() => open(skill.slug)}
            >
              <div className="dagent-skill-card__top">
                <BookOpen size={14} aria-hidden="true" />
                <strong>{skill.name}</strong>
              </div>
              <p>{skill.description}</p>
              <div className="dagent-skill-card__meta">
                <span className={`dagent-badge dagent-badge--${skill.source.toLowerCase()}`}>{SOURCE_LABEL[skill.source] || skill.source}</span>
                <span>v{skill.version}</span>
                <span>used {skill.usageCount}×</span>
                {skill.manualMinutes > 0 && <span>~{skill.manualMinutes} min by hand</span>}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="dagent-skills__detail">
        {mode === 'create' && (
          <SkillForm
            initial={EMPTY_FORM}
            creating
            toolNames={toolNames}
            onCancel={() => setMode('view')}
            onSaved={(skill) => {
              load();
              open(skill.slug);
            }}
          />
        )}
        {mode === 'edit' && selected && (
          <SkillForm
            initial={{
              slug: selected.slug,
              name: selected.name,
              description: selected.description,
              whenToUse: selected.whenToUse || '',
              instructions: selected.instructions,
              tools: (selected.tools || []).join(', '),
              manualMinutes: String(selected.manualMinutes ?? ''),
            }}
            creating={false}
            toolNames={toolNames}
            onCancel={() => setMode('view')}
            onSaved={() => {
              load();
              open(selected.slug);
            }}
          />
        )}
        {mode === 'view' && !selected && <p className="dagent-muted dagent-pad">Pick a skill to read it, or create a new one.</p>}
        {mode === 'view' && selected && (
          <div className="dagent-skill-detail">
            <div className="dagent-panel__bar">
              <div>
                <div className="dagent-kicker">
                  {SOURCE_LABEL[selected.source]} · version {selected.version} · <span className="dagent-mono">{selected.slug}</span>
                </div>
                <h3>{selected.name}</h3>
              </div>
              <span className="dagent-spacer" />
              {onUseSkill && selected.status === 'ACTIVE' && (
                <button type="button" className="dagent-btn dagent-btn--primary dagent-btn--sm" onClick={() => onUseSkill(`Use the ${selected.slug} skill`)}>
                  <Wand2 size={13} aria-hidden="true" /> Use in chat
                </button>
              )}
              {canEdit(selected) && (
                <>
                  <button type="button" className="dagent-btn dagent-btn--ghost dagent-btn--sm" onClick={() => setMode('edit')}>
                    <Pencil size={13} aria-hidden="true" /> Edit
                  </button>
                  {selected.status === 'ACTIVE' && (
                    <button type="button" className="dagent-btn dagent-btn--ghost dagent-btn--sm" onClick={archive}>
                      <Archive size={13} aria-hidden="true" /> Archive
                    </button>
                  )}
                </>
              )}
            </div>
            <p>{selected.description}</p>
            {selected.whenToUse && (
              <p className="dagent-muted">
                <strong>When to use:</strong> {selected.whenToUse}
              </p>
            )}
            {selected.tools?.length > 0 && (
              <div className="dagent-chips">
                {selected.tools.map((tool) => (
                  <span key={tool} className="dagent-chip dagent-mono">
                    {tool}
                  </span>
                ))}
              </div>
            )}
            <div className="dagent-preview-box">
              <MarkdownText text={selected.instructions} />
            </div>
            {selected.history?.length > 0 && (
              <>
                <h5 className="dagent-subhead">Earlier versions</h5>
                <ul className="dagent-history">
                  {[...selected.history].reverse().map((entry) => (
                    <li key={entry.version}>
                      <span>
                        v{entry.version} · {entry.name}
                      </span>
                      <span className="dagent-muted">{timeAgo(entry.updatedAt)}</span>
                      {canEdit(selected) && (
                        <button type="button" className="dagent-link-btn" onClick={() => restore(entry.version)}>
                          <RotateCcw size={12} aria-hidden="true" /> Restore
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default SkillsManager;
